import { useWorkflowTemplates } from '@commenda/api';
import {
  Button,
  FieldWrapper,
  Icons,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleCombobox,
  useToggle,
} from '@commenda/kakashi';
import {
  SubWorkflowStepMode,
  TBatchWorkflowStepDefinition,
  TSubWorkflowStepDefinition,
} from '@commenda/types';
import {
  ExpressionType,
  TDataSourceCall,
  TMapping,
} from '@commenda/types/src/workflow/dsl';
import { useState } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { ExprEditor } from '../editors/ExperssionEditor';
import { IteratorEditor } from '../editors/IteratorEditor';
import { AddEditDataSourceModal } from '../modals/AddEditDataSourceModal';
import { MappingModal } from '../modals/AddEditMappingModal';

export const SubWorkflowNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { data: workflowTemplatesResponse } = useWorkflowTemplates();
  const workflowTemplates = workflowTemplatesResponse?.workflowTemplates;

  const addEditDataSourceModalVisibility = useToggle(false);

  const { workflow, setWorkflow } = useWorkflow();

  const [dataSourceToEdit, setDataSourceToEdit] =
    useState<TDataSourceCall | null>(null);
  const [mappingToEdit, setMappingToEdit] = useState<TMapping | null>(null);
  const addEditMappingModalVisibility = useToggle(false);

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TSubWorkflowStepDefinition;

  const childWorkflowTemplateOptions = workflowTemplates
    ? workflowTemplates
        .filter((workflowTemplate) => workflowTemplate.activeVersionId)
        .map((workflowTemplate) => ({
          label: workflowTemplate.name,
          value: workflowTemplate.activeVersionId,
        }))
    : [];

  const updateSubWorkflowStep = (
    updates: Partial<TSubWorkflowStepDefinition>
  ) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const onSaveDataSource = (dataSource: TDataSourceCall) => {
    const existingDataSource = selectedStep.dataSources?.find(
      (ds) => ds.id === dataSource.id
    );
    if (existingDataSource)
      updateSubWorkflowStep({
        dataSources: selectedStep.dataSources?.map((ds) =>
          ds.id === dataSource.id ? dataSource : ds
        ),
      });
    else
      updateSubWorkflowStep({
        dataSources: [...(selectedStep.dataSources || []), dataSource],
      });
  };

  const onSaveMapping = (mapping: TMapping) => {
    const existingMapping = selectedStep.inputMapping?.find(
      (m) => m.id === mapping.id
    );
    if (existingMapping)
      updateSubWorkflowStep({
        inputMapping: selectedStep.inputMapping?.map((m) =>
          m.id === mapping.id ? mapping : m
        ),
      });
    else
      updateSubWorkflowStep({
        inputMapping: [...(selectedStep.inputMapping || []), mapping],
      });
  };

  if (!selectedStep) return <div>Step not found</div>;

  return (
    <div className="flex flex-col gap-4">
      <FieldWrapper>
        <Label>Type</Label>
        <Select
          value={selectedStep.mode}
          onValueChange={(value) => {
            const newMode = value as SubWorkflowStepMode;

            if (newMode === SubWorkflowStepMode.SINGLE) {
              setWorkflow({
                ...workflow,
                steps: workflow?.steps.map((step) => {
                  if (step.id !== selectedEntity.id) return step;
                  const nextStep = {
                    ...(step as TSubWorkflowStepDefinition),
                    mode: newMode,
                  } as TSubWorkflowStepDefinition & { iterable?: unknown };
                  delete nextStep.iterable;
                  return nextStep;
                }),
              });
              return;
            }

            updateSubWorkflowStep({ mode: newMode });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SubWorkflowStepMode.SINGLE}>Single</SelectItem>
            <SelectItem value={SubWorkflowStepMode.BATCH}>Batch</SelectItem>
          </SelectContent>
        </Select>
      </FieldWrapper>
      <FieldWrapper>
        <Label>Child Workflow Template</Label>
        <SingleCombobox
          options={childWorkflowTemplateOptions}
          value={selectedStep.childWorkflowTemplateVersionId}
          setValue={(value) => {
            updateSubWorkflowStep({ childWorkflowTemplateVersionId: value });
          }}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Label className="flex justify-between items-center ">
          <span className="grow">Data Sources</span>
          <Button
            size="sm"
            onClick={() => {
              setDataSourceToEdit(null);
              addEditDataSourceModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
          </Button>
        </Label>
        {selectedStep.dataSources && selectedStep.dataSources.length > 0 ? (
          <div className="flex flex-col gap-2">
            {selectedStep.dataSources.map((dataSource) => (
              <div
                key={dataSource.id}
                className="flex items-center justify-between gap-2"
              >
                <span>{dataSource.alias}</span>
                <div className="flex items-center gap-2">
                  <Icons.Edit01
                    className="size-4"
                    onClick={() => {
                      setDataSourceToEdit(dataSource);
                      addEditDataSourceModalVisibility.show();
                    }}
                  />
                  <Icons.Trash02
                    className="size-4"
                    onClick={() => {
                      updateSubWorkflowStep({
                        dataSources: selectedStep.dataSources.filter(
                          (ds) => ds.id !== dataSource.id
                        ),
                      });
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>No data sources</div>
        )}
      </FieldWrapper>

      {selectedStep.mode === SubWorkflowStepMode.BATCH && (
        <FieldWrapper>
          <Label>Iterator</Label>
          <IteratorEditor
            iterator={(selectedStep as TBatchWorkflowStepDefinition).iterable}
            onChange={(iterator) => {
              updateSubWorkflowStep({ iterable: iterator });
            }}
            workflowDefinition={workflow}
            stepId={selectedEntity.id}
          />
        </FieldWrapper>
      )}

      <FieldWrapper>
        <Label className="flex justify-between items-center ">
          <span className="grow">Input Mappings</span>
          <Button
            size="sm"
            onClick={() => {
              setMappingToEdit(null);
              addEditMappingModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
          </Button>
        </Label>
        <div className="flex flex-col gap-2">
          {selectedStep.inputMapping?.map((mapping) => (
            <div
              key={mapping.id}
              className="flex items-center justify-between gap-2"
            >
              <span>{mapping.targetPath}</span>
              <div className="flex items-center gap-2">
                <Icons.Edit01
                  className="size-4"
                  onClick={() => {
                    setMappingToEdit(mapping);
                    addEditMappingModalVisibility.show();
                  }}
                />
                <Icons.Trash02
                  className="size-4"
                  onClick={() => {
                    updateSubWorkflowStep({
                      inputMapping: selectedStep.inputMapping?.filter(
                        (m) => m.id !== mapping.id
                      ),
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </FieldWrapper>

      <FieldWrapper>
        <Label>Child Workflow Name</Label>
        <ExprEditor
          value={
            selectedStep.childWorkflowName || {
              type: ExpressionType.JSONATA,
              src: '',
            }
          }
          onChange={(value) => {
            updateSubWorkflowStep({ childWorkflowName: value });
          }}
        />
      </FieldWrapper>

      <AddEditDataSourceModal
        isOpen={addEditDataSourceModalVisibility.visible}
        onClose={() => {
          addEditDataSourceModalVisibility.hide();
          setDataSourceToEdit(null);
        }}
        dataSource={dataSourceToEdit}
        onSave={onSaveDataSource}
        workflowDefinition={workflow}
      />

      <MappingModal
        isOpen={addEditMappingModalVisibility.visible}
        onClose={() => {
          addEditMappingModalVisibility.hide();
          setMappingToEdit(null);
        }}
        mapping={mappingToEdit}
        onMappingChange={onSaveMapping}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />
    </div>
  );
};
